import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, DoorOpen, DoorClosed, Building2, Users, Ban, RotateCcw, ArrowLeft, Webhook, Play, X as XIcon, AlertTriangle, Activity, Sparkles, Server, Database, ChevronRight, MessageCircle, TrendingUp, Mail, Edit3, Send, Trash2, HardDrive } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { AdminCreateCompanyDialog, AdminChangeOwnerDialog } from "@/components/admin/AdminCreateCompanyDialog";
import AdminBackups from "@/components/admin/AdminBackups";
import AdminMoneyIntegrity from "@/components/admin/AdminMoneyIntegrity";
import AdminAccountingEngineCard from "@/components/admin/AdminAccountingEngineCard";
import AdminPlatformScheduler from "@/components/admin/AdminPlatformScheduler";
import AdminAiUsageCard from "@/components/admin/AdminAiUsageCard";
import AdminAiBillingCard from "@/components/admin/AdminAiBillingCard";
import SystemHealthCard from "@/components/admin/SystemHealthCard";
import StorageSystemConfigCard from "@/components/admin/StorageSystemConfigCard";
import AiProvidersCard from "@/components/admin/AiProvidersCard";
import { GoogleCloudCostCard } from "@/components/admin/GoogleCloudCostCard";
import AiBenchmarkCard from "@/components/admin/AiBenchmarkCard";
import AiModelReportCard from "@/components/admin/AiModelReportCard";
import AiFunctionCheckCard from "@/components/admin/AiFunctionCheckCard";
import AiHealthDashboardCard from "@/components/admin/AiHealthDashboardCard";
import SystemAdminTab from "@/components/admin/SystemAdminTab";
import WhatsAppSystemConfigCard from "@/components/admin/WhatsAppSystemConfigCard";
import BillingOverviewCard from "@/components/admin/BillingOverviewCard";
import AdminPlansEditor from "@/components/admin/AdminPlansEditor";
import { SettingsGlobalAllowlistSection } from "@/components/SettingsGlobalAllowlistSection";
import SmtpSystemConfigCard from "@/components/admin/SmtpSystemConfigCard";
import ZefixSystemConfigCard from "@/components/admin/ZefixSystemConfigCard";
import CentralInboxSystemConfigCard from "@/components/admin/CentralInboxSystemConfigCard";
import IntegrationSpecCard from "@/components/admin/IntegrationSpecCard";
import AbnahmeReportCard from "@/components/admin/AbnahmeReportCard";
import IntegrationWatchCard from "@/components/admin/IntegrationWatchCard";
import EmailTemplatesEditor from "@/components/admin/EmailTemplatesEditor";

import { formatDateTime } from "@/lib/formatDate";

function fmtDate(iso) {
  return formatDateTime(iso) || "—";
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section") || "";
  const goSection = (id) => {
    if (id) setSearchParams({ section: id });
    else setSearchParams({});
  };
  const shouldShow = (id) => section === id;
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState(null);
  // Iter59: Webhook-Retry-Queue UI state
  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState("pending");
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueBusyId, setQueueBusyId] = useState(null);
  const [workerRunning, setWorkerRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/auth/me");
        setMe(r.data);
        if (!r.data?.is_super_admin) {
          toast.error("Super-Admin-Berechtigung erforderlich");
          navigate("/");
          return;
        }
      } catch {
        navigate("/login");
        return;
      } finally {
        setAuthChecked(true);
      }
      await refresh();
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c, u] = await Promise.all([
        apiClient.get("/admin/system-settings"),
        apiClient.get("/admin/companies"),
        apiClient.get("/admin/users"),
      ]);
      setSettings(s.data);
      setCompanies(c.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error("Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const toggleRegistration = async (enabled) => {
    setSavingSettings(true);
    try {
      const r = await apiClient.put("/admin/system-settings", { registration_enabled: enabled });
      setSettings(r.data);
      toast.success(enabled ? "Registrierung geöffnet" : "Registrierung geschlossen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingSettings(false);
    }
  };

  const setUserDisabled = async (user_id, disabled) => {
    if (disabled && !window.confirm("User wirklich sperren? Alle vertrauenswürdigen Geräte werden invalidiert.")) return;
    setBusyUserId(user_id);
    try {
      await apiClient.post(`/admin/users/${user_id}/${disabled ? "disable" : "enable"}`);
      toast.success(disabled ? "User gesperrt" : "User entsperrt");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusyUserId(null);
    }
  };

  // Iter136: SA kann E-Mail-Adresse eines Users ändern (auch die eigene)
  const changeUserEmail = async (u) => {
    const current = u.email || "";
    const next = window.prompt(
      `E-Mail für ${current} ändern.\n\nNeue E-Mail (leer lassen zum Abbrechen):`,
      current
    );
    if (!next || next.trim() === "" || next.trim().toLowerCase() === current.toLowerCase()) return;
    setBusyUserId(u.id);
    try {
      const r = await apiClient.put(`/admin/users/${u.id}/email`, { email: next.trim() });
      toast.success(r.data?.unchanged ? "E-Mail unverändert" : `E-Mail geändert: ${r.data.new_email}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "E-Mail-Änderung fehlgeschlagen");
    } finally {
      setBusyUserId(null);
    }
  };

  // Iter256: Login-Freigabe (global_allowlist) direkt aus der Benutzer-Verwaltung
  const allowUserLogin = async (u) => {
    setBusyUserId(u.id);
    try {
      await apiClient.post("/admin/global-allowlist", {
        email: u.email, role: "viewer", note: "Freigabe via Benutzer-Verwaltung",
      });
      toast.success(`${u.email} für den Login freigegeben`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally {
      setBusyUserId(null);
    }
  };

  // Iter257: Verwaiste User (0 Firmen) endgültig löschen
  const deleteUser = async (u) => {
    if (!window.confirm(`${u.email} endgültig löschen?\n\nFreigaben, Login-Codes und Geräte werden ebenfalls entfernt. Das kann nicht rückgängig gemacht werden.`)) return;
    setBusyUserId(u.id);
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} gelöscht`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setBusyUserId(null);
    }
  };

  // ---------- Iter59: Webhook-Retry-Queue ----------

  const loadQueue = async (silent = false) => {
    if (!silent) setQueueLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (queueFilter !== "all") params.set("status", queueFilter);
      const r = await apiClient.get(`/scheduler/webhook-retry/queue?${params.toString()}`);
      setQueue(r.data || []);
    } catch (e) {
      if (!silent) toast.error("Queue konnte nicht geladen werden");
    } finally {
      if (!silent) setQueueLoading(false);
    }
  };

  const triggerWorker = async () => {
    setWorkerRunning(true);
    try {
      const r = await apiClient.post("/scheduler/webhook-retry/run-now");
      const d = r.data;
      toast.success(
        `Worker: ${d.processed || 0} verarbeitet · ${d.succeeded || 0} ok · ${d.failed_retried || 0} re-queued · ${d.dead || 0} dead`
      );
      await loadQueue();
    } catch (e) {
      toast.error("Worker-Trigger fehlgeschlagen");
    } finally {
      setWorkerRunning(false);
    }
  };

  const retryNow = async (itemId) => {
    setQueueBusyId(itemId);
    try {
      await apiClient.post(`/scheduler/webhook-retry/queue/${itemId}/retry-now`);
      toast.success("Retry ausgelöst");
      await loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry fehlgeschlagen");
    } finally {
      setQueueBusyId(null);
    }
  };

  const cancelItem = async (itemId) => {
    if (!window.confirm("Item wirklich abbrechen? Es wird nicht mehr verarbeitet.")) return;
    setQueueBusyId(itemId);
    try {
      await apiClient.post(`/scheduler/webhook-retry/queue/${itemId}/cancel`);
      toast.success("Item abgebrochen");
      await loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abbrechen fehlgeschlagen");
    } finally {
      setQueueBusyId(null);
    }
  };

  // Live-Polling alle 10s (silent)
  useEffect(() => {
    if (!authChecked || !me?.is_super_admin) return;
    loadQueue();
    const id = setInterval(() => loadQueue(true), 10000);
    return () => clearInterval(id);
  }, [authChecked, me?.is_super_admin, queueFilter]);

  if (!authChecked) return null;

  const regOn = !!settings?.registration_enabled;

  // Iter130: Grid-Kacheln als Landing-Seite
  const TILES = [
    {
      id: "health",
      title: "System-Health",
      desc: "MongoDB · Scheduler · Uptime · Cron-Jobs",
      icon: Activity,
      color: "emerald",
      metric: "Live",
    },
    {
      id: "ai",
      title: "KI-Provider",
      desc: "OpenAI · Anthropic · Gemini · Benchmark",
      icon: Sparkles,
      color: "violet",
    },
    {
      id: "storage",
      title: "Datei-Speicher (S3)",
      desc: "Eigener Bucket · Infomaniak / Hetzner · Migration",
      icon: HardDrive,
      color: "slate",
    },
    {
      id: "users",
      title: "Benutzer-Verwaltung",
      desc: "Alle User im System · Sperren · Entsperren",
      icon: Users,
      color: "indigo",
      metric: `${users.length}`,
    },
    {
      id: "companies",
      title: "Firmen-Übersicht",
      desc: "Alle Mandanten im System · Owner · Demo-Firmen",
      icon: Building2,
      color: "blue",
      metric: `${companies.length}`,
    },
    {
      id: "webhooks",
      title: "Webhook-Queue",
      desc: "Retry-Cockpit · Exp. Backoff · Dead-Letter",
      icon: Webhook,
      color: "amber",
    },
    {
      id: "backups",
      title: "Backups & Restore",
      desc: "DB-Backups · Cloud-Sync · Restore-Point",
      icon: Database,
      color: "slate",
    },
    {
      id: "system",
      title: "System-Verwaltung",
      desc: "Registrierung · Full-Reset · Archiv-Export",
      icon: Server,
      color: "rose",
      metric: regOn ? "OFFEN" : "GESCHL.",
    },
    {
      id: "whatsapp",
      title: "WhatsApp / Meta",
      desc: "Business-Nummer · Access Token · Webhook",
      icon: MessageCircle,
      color: "emerald",
    },
    {
      id: "billing",
      title: "Abos & Umsatz",
      desc: "MRR · ARR · Trials · Churn · Überfällige",
      icon: TrendingUp,
      color: "indigo",
    },
    {
      id: "allowlist",
      title: "E-Mail-Freigabe",
      desc: "Whitelist · Wer darf sich registrieren",
      icon: Mail,
      color: "blue",
    },
    {
      id: "smtp",
      title: "SMTP / E-Mail-Versand",
      desc: "Server · Login · Absender · Test-Mail",
      icon: Send,
      color: "amber",
    },
    {
      id: "zefix",
      title: "Zefix Handelsregister",
      desc: "Offizieller REST-Zugang · Adressen aus HR",
      icon: Building2,
      color: "indigo",
    },
    {
      id: "central_inbox",
      title: "Central E-Mail-Inbox",
      desc: "Zentrale Belege-Adresse · Sender-Routing · Auto-Bounce",
      icon: Send,
      color: "rose",
    },
    {
      id: "integration_spec",
      title: "Integrations-Spez. (extern)",
      desc: "Onboarding-Doku für Dritt-Apps · Download",
      icon: Webhook,
      color: "slate",
    },
    {
      id: "abnahme_report",
      title: "Abnahme-Handbuch",
      desc: "Alle Testabläufe & Protokolle Phase 1–4 · Download",
      icon: ShieldCheck,
      color: "emerald",
    },
    {
      id: "integration_watch",
      title: "Integrations-Wächter",
      desc: "E-Mail · WhatsApp · EZB-Kurse · Bank · KI – Alarm bei Störung",
      icon: Activity,
      color: "rose",
    },
  ];

  const TILE_COLORS = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 group-hover:bg-emerald-100",
    violet: "bg-violet-50 text-violet-700 border-violet-200 group-hover:bg-violet-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200 group-hover:bg-indigo-100",
    blue: "bg-blue-50 text-blue-700 border-blue-200 group-hover:bg-blue-100",
    amber: "bg-amber-50 text-amber-700 border-amber-200 group-hover:bg-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200 group-hover:bg-slate-100",
    rose: "bg-rose-50 text-rose-700 border-rose-200 group-hover:bg-rose-100",
  };

  const activeTile = TILES.find((t) => t.id === section);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="super-admin-page">
      {/* Header + Back */}
      {section ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => goSection("")}
            className="text-slate-600"
            data-testid="super-admin-back-to-grid"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Zurück zum Cockpit
          </Button>
          {activeTile && (
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center border ${TILE_COLORS[activeTile.color] || TILE_COLORS.slate}`}>
                <activeTile.icon className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{activeTile.title}</h1>
                <p className="mt-0.5 text-sm text-slate-500">{activeTile.desc}</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
            data-testid="super-admin-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Zurück
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Super-Admin</h1>
              <p className="mt-0.5 text-sm text-slate-500">System-weite Verwaltung — sichtbar nur für dich.</p>
            </div>
          </div>

          {/* Kachel-Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="super-admin-grid">
            {TILES.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.id}
                  onClick={() => goSection(tile.id)}
                  data-testid={`super-admin-tile-${tile.id}`}
                  className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-slate-300 transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`h-11 w-11 rounded-lg flex items-center justify-center border transition-colors ${TILE_COLORS[tile.color] || TILE_COLORS.slate}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    {tile.metric && (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                        {tile.metric}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{tile.title}</h3>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{tile.desc}</p>
                  <div className="mt-3 flex items-center text-xs text-slate-400 group-hover:text-slate-700 transition-colors">
                    Öffnen <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Iter70: System-Health */}
      {shouldShow("health") && <SystemHealthCard />}
      {shouldShow("storage") && <StorageSystemConfigCard />}

      {/* Iter79: KI-Provider-Keys */}
      {shouldShow("ai") && <AiProvidersCard />}
      {shouldShow("ai") && <AdminAiUsageCard />}
      {shouldShow("ai") && <AdminAiBillingCard />}
      {shouldShow("ai") && <GoogleCloudCostCard />}
      {shouldShow("ai") && <AiFunctionCheckCard />}
      {shouldShow("ai") && <AiModelReportCard />}

      {/* Iter195: KI-Gesundheits-Dashboard */}
      {shouldShow("ai") && <AiHealthDashboardCard />}

      {/* Iter79.6: AI-Benchmark */}
      {shouldShow("ai") && <AiBenchmarkCard />}

      {/* Iter131: WhatsApp / Meta System-Konfiguration */}
      {shouldShow("whatsapp") && <WhatsAppSystemConfigCard />}

      {/* Iter133: Abos & Umsatz Übersicht */}
      {shouldShow("billing") && <BillingOverviewCard />}

      {/* Iter134: Pakete verwalten (Preise, Limits, Features, Sichtbarkeit) */}
      {shouldShow("billing") && <AdminPlansEditor />}

      {/* Iter136: E-Mail-Freigabe (Global Allowlist) */}
      {shouldShow("allowlist") && <SettingsGlobalAllowlistSection isSuperAdmin={true} />}

      {/* Iter138: System-SMTP-Konfiguration */}
      {shouldShow("smtp") && <SmtpSystemConfigCard />}

      {/* Iter139: E-Mail-Vorlagen (unterhalb SMTP-Config) */}
      {shouldShow("smtp") && <EmailTemplatesEditor />}

      {/* Iter144: Zefix Handelsregister-Zugangsdaten (SA-only) */}
      {shouldShow("zefix") && <ZefixSystemConfigCard />}

      {/* Iter152: Central E-Mail-Inbox (SA-only) */}
      {shouldShow("central_inbox") && <CentralInboxSystemConfigCard />}

      {/* Iter256: Integrations-Spezifikation für Dritt-Apps (SA-only) */}
      {shouldShow("integration_spec") && <IntegrationSpecCard />}
      {shouldShow("abnahme_report") && <AbnahmeReportCard />}
      {shouldShow("integration_watch") && <IntegrationWatchCard />}

      {/* Iter81: System-Verwaltung */}
      {shouldShow("system") && <SystemAdminTab me={me} />}

      {/* Card 1: Registration Toggle */}
      {shouldShow("system") && (
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="super-admin-registration-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${regOn ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
              {regOn ? <DoorOpen className="h-6 w-6" /> : <DoorClosed className="h-6 w-6" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Öffentliche Registrierung</h2>
              <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
                {regOn
                  ? "Aktuell offen — jede:r kann sich auf /register ein Konto anlegen."
                  : "Aktuell geschlossen — neue Registrierungen werden mit 403 abgelehnt. Nur eingeladene/eingerichtete User können sich einloggen."}
              </p>
              {settings?.updated_at && (
                <p className="text-xs text-slate-400 mt-1.5">
                  Zuletzt geändert: {fmtDate(settings.updated_at)} von {settings.updated_by || "—"}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${regOn ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
              {regOn ? "OFFEN" : "GESCHLOSSEN"}
            </span>
            <Switch
              checked={regOn}
              onCheckedChange={toggleRegistration}
              disabled={savingSettings}
              data-testid="super-admin-registration-toggle"
            />
          </div>
        </div>
      </Card>
      )}

      {/* Card 2: Companies */}
      {shouldShow("companies") && (
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="super-admin-companies-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Alle Firmen im System ({companies.length})</h2>
          </div>
          <div className="flex items-center gap-2">
            <AdminCreateCompanyDialog users={users} onCreated={refresh} />
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="super-admin-refresh-companies">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Lädt …</p>
        ) : companies.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Firmen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="super-admin-companies-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-4">Firma</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Demo</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4">Erstellt</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`super-admin-company-row-${c.id}`}>
                    <td className="py-2.5 pr-4 font-medium text-slate-900">{c.name}</td>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {c.owner_email || "—"}
                      {c.owner_disabled && (
                        <span className="ml-1.5 text-[10px] text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">gesperrt</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-600">{c.user_count}</td>
                    <td className="py-2.5 pr-4">
                      {c.onboarding_demo && (
                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Demo</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">
                      <select
                        className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                        value={c.plan_id || "free"}
                        onChange={async (e) => {
                          try {
                            const r = await apiClient.put(`/admin/companies/${c.id}/plan`, { plan_id: e.target.value, months_valid: 12 });
                            toast.success(r.data.detail);
                            refresh();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Plan-Wechsel fehlgeschlagen");
                          }
                        }}
                        data-testid={`admin-company-plan-select-${c.id}`}
                      >
                        <option value="free">free</option>
                        <option value="starter">starter</option>
                        <option value="pro">pro</option>
                        <option value="enterprise">enterprise</option>
                        <option value="unlimited">unbegrenzt (Admin)</option>
                      </select>
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="py-2.5 pr-2 text-right">
                      <AdminChangeOwnerDialog company={c} users={users} onChanged={refresh} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {/* Card 3: Users */}
      {shouldShow("users") && (
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="super-admin-users-card">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">Alle User ({users.length})</h2>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Lädt …</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="super-admin-users-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="py-2 pr-4">E-Mail</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Firmen</th>
                  <th className="py-2 pr-4">Registriert</th>
                  <th className="py-2 pr-4 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`super-admin-user-row-${u.id}`}>
                      <td className="py-2.5 pr-4 font-medium text-slate-900">
                        {u.email}
                        {isMe && <span className="ml-1.5 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">du</span>}
                      </td>
                      <td className="py-2.5 pr-4">
                        {u.is_super_admin && (
                          <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded mr-1">Super-Admin</span>
                        )}
                        {u.disabled ? (
                          <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded">gesperrt</span>
                        ) : (
                          <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">aktiv</span>
                        )}
                        {!u.is_super_admin && !u.login_allowed && (
                          <span className="ml-1 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`super-admin-not-allowed-${u.id}`}>
                            Login nicht freigegeben
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600">{u.company_count}</td>
                      <td className="py-2.5 pr-4 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="py-2.5 pr-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyUserId === u.id}
                          onClick={() => changeUserEmail(u)}
                          className="text-slate-600 hover:text-indigo-700 mr-1"
                          data-testid={`super-admin-edit-email-${u.id}`}
                          title="E-Mail-Adresse ändern"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        {!u.is_super_admin && !u.login_allowed && !u.disabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyUserId === u.id}
                            onClick={() => allowUserLogin(u)}
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-1"
                            data-testid={`super-admin-allow-login-${u.id}`}
                            title="Login-Code für diese E-Mail freigeben"
                          >
                            Freigeben
                          </Button>
                        )}
                        {!isMe && !u.is_super_admin && (
                          u.disabled ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyUserId === u.id}
                              onClick={() => setUserDisabled(u.id, false)}
                              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              data-testid={`super-admin-enable-user-${u.id}`}
                            >
                              Entsperren
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyUserId === u.id}
                              onClick={() => setUserDisabled(u.id, true)}
                              className="text-rose-700 border-rose-200 hover:bg-rose-50"
                              data-testid={`super-admin-disable-user-${u.id}`}
                            >
                              <Ban className="h-3.5 w-3.5 mr-1" />
                              Sperren
                            </Button>
                          )
                        )}
                        {!isMe && !u.is_super_admin && u.company_count === 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyUserId === u.id}
                            onClick={() => deleteUser(u)}
                            className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 ml-1"
                            data-testid={`super-admin-delete-user-${u.id}`}
                            title="User endgültig löschen (nur ohne Firmen möglich)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {/* Iter59: Webhook-Retry-Queue Cockpit */}
      {shouldShow("webhooks") && (
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="super-admin-webhook-queue-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
              <Webhook className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Webhook-Retry-Queue</h2>
              <p className="text-xs text-slate-500">
                Outbound-Webhooks die fehlgeschlagen sind — automatischer Retry mit Exp. Backoff (1m → 5m → 30m → 2h → 12h)
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={triggerWorker}
            disabled={workerRunning}
            data-testid="webhook-queue-trigger-worker"
          >
            <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${workerRunning ? "animate-spin" : ""}`} />
            {workerRunning ? "Läuft …" : "Worker jetzt ausführen"}
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="webhook-queue-filters">
          {[
            { id: "pending", label: "Wartend", color: "bg-amber-50 text-amber-800 border-amber-200" },
            { id: "dead", label: "Tot", color: "bg-rose-50 text-rose-800 border-rose-200" },
            { id: "succeeded", label: "Erfolgreich", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
            { id: "all", label: "Alle", color: "bg-slate-50 text-slate-700 border-slate-200" },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setQueueFilter(f.id)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                queueFilter === f.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : `${f.color} hover:bg-opacity-75`
              }`}
              data-testid={`webhook-queue-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-400">
            Auto-Refresh alle 10s · Worker-Cron alle 60s
          </span>
        </div>

        {/* Queue Table */}
        <div className="mt-4 -mx-5 overflow-x-auto">
          {queueLoading ? (
            <div className="px-5 py-8 text-center text-xs text-slate-400">Lade Queue …</div>
          ) : queue.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-400" data-testid="webhook-queue-empty">
              {queueFilter === "pending"
                ? "Keine wartenden Webhooks. ✓"
                : queueFilter === "dead"
                ? "Keine toten Webhooks. ✓"
                : "Keine Einträge."}
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="webhook-queue-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-2">Status</th>
                  <th className="px-2 py-2">Event</th>
                  <th className="px-2 py-2">Target</th>
                  <th className="px-2 py-2 text-center">Versuche</th>
                  <th className="px-2 py-2">Nächster Retry</th>
                  <th className="px-2 py-2">Letzter Fehler</th>
                  <th className="px-5 py-2 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item) => {
                  const statusColor = {
                    pending: "bg-amber-100 text-amber-900",
                    processing: "bg-blue-100 text-blue-900",
                    succeeded: "bg-emerald-100 text-emerald-900",
                    dead: "bg-rose-100 text-rose-900",
                    canceled: "bg-slate-200 text-slate-700",
                  }[item.status] || "bg-slate-100 text-slate-700";
                  return (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`webhook-queue-row-${item.id}`}
                    >
                      <td className="px-5 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>
                          {item.status === "dead" && <AlertTriangle className="h-2.5 w-2.5" />}
                          {item.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px]">{item.event}</td>
                      <td className="px-2 py-2 max-w-[200px] truncate text-slate-600" title={item.target_url}>
                        {item.target_url}
                      </td>
                      <td className="px-2 py-2 text-center tabular-nums">
                        {item.queue_attempts || 0}
                        <span className="text-slate-400">/5</span>
                      </td>
                      <td className="px-2 py-2 text-slate-600 whitespace-nowrap">
                        {item.status === "pending" ? fmtDate(item.next_retry_at) : "—"}
                      </td>
                      <td className="px-2 py-2 max-w-[260px] truncate text-rose-700" title={item.last_error}>
                        {item.last_status ? `${item.last_status} · ` : ""}
                        {item.last_error || "—"}
                      </td>
                      <td className="px-5 py-2 text-right whitespace-nowrap">
                        {(item.status === "pending" || item.status === "dead") && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => retryNow(item.id)}
                            disabled={queueBusyId === item.id}
                            className="h-7 px-2 mr-1"
                            data-testid={`webhook-queue-retry-${item.id}`}
                          >
                            <Play className="h-3 w-3 mr-1" />
                            Retry
                          </Button>
                        )}
                        {(item.status === "pending" || item.status === "dead") && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => cancelItem(item.id)}
                            disabled={queueBusyId === item.id}
                            className="h-7 px-2 text-rose-700 hover:text-rose-900 hover:bg-rose-50"
                            data-testid={`webhook-queue-cancel-${item.id}`}
                          >
                            <XIcon className="h-3 w-3 mr-1" />
                            Abbrechen
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>
      )}

      {/* Card 5: DB-Backups & Cloud-Sync (Iter63) */}
      {shouldShow("backups") && <AdminBackups />}
      {shouldShow("backups") && <AdminMoneyIntegrity />}
      {shouldShow("backups") && <AdminAccountingEngineCard />}
      {shouldShow("backups") && <AdminPlatformScheduler />}
    </div>
  );
}
